// Filtros del cuadro de mando (change-001): rama, motivo y desenlace.
// Puros y en cliente: se aplican a la lista ya cargada del periodo y las
// agregaciones la reciben ya filtrada, así que ningún contrato cambia.

import {
  estadoVisual,
  etiquetaMotivo,
  etiquetaRama,
  nombreDeCliente,
} from '@/lib/llamadas/presentacion';
import type { LlamadaFeedRow } from '@/types/llamadas';

export type FiltroDesenlace = 'resueltas' | 'escaladas' | 'en_curso';

// Valor centinela del filtro de cliente para "sin cliente identificado" (número
// oculto, particular desconocido). Es un valor más del mismo campo y no un
// booleano aparte: así `FiltrosCuadro` sigue siendo un registro plano de
// `string | null`, que es lo que asumen el helper `alterna` de la UI y el botón
// "Limpiar". Con dos campos habría un estado imposible (un uuid concreto Y
// "sin cliente" a la vez) que habría que defender.
export const SIN_CLIENTE = '__sin_cliente__';

export interface FiltrosCuadro {
  rama: string | null;
  motivo: string | null;
  desenlace: FiltroDesenlace | null;
  cliente: string | null; // uuid de clientes_replica, o SIN_CLIENTE
}

export const SIN_FILTROS: FiltrosCuadro = {
  rama: null,
  motivo: null,
  desenlace: null,
  cliente: null,
};

// `ETIQUETA_RAMA` y `etiquetaRama` nacieron aquí y viven ahora en
// `lib/llamadas/presentacion.ts`: el detalle de Atención también las necesita y Atención
// no puede depender de `lib/cuadro`. El motivo de la mudanza está escrito allí.

export const ETIQUETA_DESENLACE: Record<FiltroDesenlace, string> = {
  resueltas: 'Resueltas IA',
  escaladas: 'Escaladas',
  en_curso: 'En curso',
};

// Los filtros activos dichos con palabras, para el informe impreso: en papel no
// hay chips, y un PDF sin su contexto no se puede interpretar.
export function describirFiltros(f: FiltrosCuadro, razonSocialCliente?: string | null): string[] {
  const partes: string[] = [];
  if (f.rama) partes.push(`Rama: ${etiquetaRama(f.rama)}`);
  if (f.motivo) partes.push(`Motivo: ${etiquetaMotivo(f.motivo)}`);
  if (f.desenlace) partes.push(`Desenlace: ${ETIQUETA_DESENLACE[f.desenlace]}`);
  if (f.cliente) {
    const quien =
      f.cliente === SIN_CLIENTE ? 'sin identificar' : (razonSocialCliente ?? 'sin nombre');
    partes.push(`Cliente: ${quien}`);
  }
  return partes;
}

// Lo que dice un bloque vacío del cuadro (sin punto final: lo pone quien lo pinta).
// Si el periodo tiene llamadas y son los filtros los que las dejan fuera, «sin
// llamadas en el periodo» sería falso.
export function textoSinLlamadas(periodoVacio: boolean): string {
  return periodoVacio ? 'Sin llamadas en el periodo' : 'Ninguna llamada cumple estos filtros';
}

// El pie de «Derivaciones». Con filtros, la cuenta es la de lo filtrado, no la del
// periodo, y la frase lo dice.
export function textoEscaladas(total: number, hayFiltros: boolean): string {
  const cuantas =
    total === 0
      ? 'Ninguna llamada escalada'
      : total === 1
        ? '1 llamada escalada'
        : `${total.toLocaleString('es-ES')} llamadas escaladas`;
  return `${cuantas} ${hayFiltros ? 'con estos filtros' : 'en el periodo'}`;
}

export function hayFiltrosActivos(f: FiltrosCuadro): boolean {
  return f.rama !== null || f.motivo !== null || f.desenlace !== null || f.cliente !== null;
}

// 'procesando' (finalizada aún sin análisis) va con "en curso": para quien mira
// el cuadro, ambas son llamadas que todavía no tienen desenlace.
function encajaDesenlace(l: LlamadaFeedRow, desenlace: FiltroDesenlace): boolean {
  const estado = estadoVisual(l);
  if (desenlace === 'resueltas') return estado === 'resuelta';
  if (desenlace === 'escaladas') return estado === 'escalada';
  return estado === 'en_curso' || estado === 'procesando';
}

// Se compara por `id_cliente` (uuid), nunca por el texto de la razón social:
// dos clientes pueden llamarse parecido y el id ya viene en la fila.
function encajaCliente(l: LlamadaFeedRow, cliente: string): boolean {
  if (cliente === SIN_CLIENTE) return l.id_cliente === null;
  return l.id_cliente === cliente;
}

export function aplicarFiltros(llamadas: LlamadaFeedRow[], f: FiltrosCuadro): LlamadaFeedRow[] {
  if (!hayFiltrosActivos(f)) return llamadas;
  return llamadas.filter(
    (l) =>
      (f.rama === null || l.rama === f.rama) &&
      (f.motivo === null || l.motivo === f.motivo) &&
      (f.desenlace === null || encajaDesenlace(l, f.desenlace)) &&
      (f.cliente === null || encajaCliente(l, f.cliente))
  );
}

export interface OpcionCliente {
  id: string; // uuid
  razonSocial: string;
  total: number; // llamadas suyas en el periodo
}

export interface OpcionesFiltro {
  ramas: string[];
  motivos: string[];
  clientes: OpcionCliente[];
  sinCliente: number; // cuántas llamadas no tienen cliente identificado
}

// Etiqueta del cliente. El embed es lo normal (hay FK), pero una fila con
// `id_cliente` y sin `cliente` dejaría un undefined que reventaría el
// localeCompare y con él todo el desplegable. Defensivo, no decorativo.
// Exportada: la exportación y el bloque de clientes nombran igual que el
// desplegable (change 003).
//
// El nombre lo pone SIEMPRE `nombreDeCliente`, que es el único sitio donde se decide
// —incluido el criterio de qué texto no vale como nombre y su límite—. Aquí no hay
// ninguna cadena de respaldo propia: tenerla es lo que hacía que cada superficie
// contestara distinto.
export function etiquetaCliente(l: LlamadaFeedRow): string {
  return nombreDeCliente(l.cliente?.razon_social ?? null, l.cliente_id_odoo);
}

// Valores presentes en el periodo cargado: evita ofrecer opciones que no
// filtrarían nada. Se derivan SIEMPRE del conjunto sin filtrar — si se
// derivasen de lo ya filtrado, al elegir un cliente desaparecerían los demás
// del desplegable y no se podría cambiar de selección.
export function opcionesDisponibles(llamadas: LlamadaFeedRow[]): OpcionesFiltro {
  const ramas = new Set<string>();
  const motivos = new Set<string>();
  const porCliente = new Map<string, OpcionCliente>();
  let sinCliente = 0;

  for (const l of llamadas) {
    if (l.rama) ramas.add(l.rama);
    if (l.motivo) motivos.add(l.motivo);

    if (l.id_cliente === null) {
      sinCliente++;
      continue;
    }
    const ya = porCliente.get(l.id_cliente);
    if (ya) ya.total++;
    else
      porCliente.set(l.id_cliente, { id: l.id_cliente, razonSocial: etiquetaCliente(l), total: 1 });
  }

  return {
    ramas: [...ramas].sort(),
    motivos: [...motivos].sort(),
    // Mismo criterio de orden que `distribucionMotivos`: alfabético en español.
    clientes: [...porCliente.values()].sort((a, b) =>
      a.razonSocial.localeCompare(b.razonSocial, 'es')
    ),
    sinCliente,
  };
}
